"use client";

/**
 * HtmlViewerPage — standalone full-viewport HTML viewer for a pop-out tab.
 */

import { useEffect } from "react";
import { FileCode } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { HtmlViewer } from "@/components/html-viewer";
import { HtmlViewerToolbar } from "@/components/html-viewer-toolbar";
import { useHtmlFile } from "@/hooks/use-html-file";
import { isXlsxFile, xlsxPreviewUrl } from "@/lib/file-links";

export interface HtmlViewerPageProps {
  /** Signed file-download URL (`/files/d/{jwt}`). */
  url: string;
  /** Filename for display (from the JWT fn claim or link label). */
  fileName: string;
  /** Called when the user wants to close the pop-out page. */
  onClose: () => void;
}

export function HtmlViewerPage({
  url,
  fileName,
  onClose,
}: HtmlViewerPageProps) {
  const { t } = useTranslation();
  const workbook = isXlsxFile(url, fileName);
  const { content, loading, error, refetch, refreshed } = useHtmlFile(workbook ? xlsxPreviewUrl(url) : url);

  useEffect(() => {
    document.title = fileName;
    return () => {
      document.title = import.meta.env.APP_NAME ?? "Layer Nine";
    };
  }, [fileName]);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileCode className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-semibold" title={fileName}>
            {fileName}
          </span>
          {refreshed && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-blue-500"
              title={t("viewer.contentUpdated")}
              data-testid="html-viewer-refreshed-indicator"
            />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <HtmlViewerToolbar content={content} fileName={fileName} sourceUrl={workbook ? url : undefined} />
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <span aria-hidden className="text-sm font-medium leading-none">
              ×
            </span>
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <Spinner className="size-5" />
            <p className="text-sm">{t("common:status.loadingDocument")}</p>
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
            <p className="text-sm text-destructive">{error}</p>
            {(error.includes("401") || error.includes("403")) && (
              <p className="text-sm text-muted-foreground">
                {t("viewer.linkExpired")}
              </p>
            )}
            <Button variant="outline" size="sm" onClick={refetch}>
              {t("common:actions.retry")}
            </Button>
          </div>
        ) : content != null ? (
          <HtmlViewer content={content} fileName={fileName} />
        ) : null}
      </div>
    </div>
  );
}
